import { baseApi } from "../baseApi";
import { pick } from "../util";
import type {
  User,
  CreateUserInput,
  UpdateUserInput,
  UserLookup,
  UserPreferences,
} from "../types";

// Fields exposed by the GraphQL `User` type (services/src/modules/user/graphql.ts).
// `organizations` is intentionally omitted — the schema does not return it yet.
const USER_FIELDS = `
  id
  prefix
  firstName
  lastName
  email
  phone
  status
  pendingDeletion {
    requestedAt
    scheduledFor
  }
  preferences {
    colorScheme
    darkMode
    language
    pushNotifications
    emailNotifications
    smsNotifications
    whatsappNotifications
    defaultOrganizationId
  }
  createdAt
  updatedAt
`;

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => ({
        body: {
          query: `
            query Users {
              users {
                ${USER_FIELDS}
              }
            }
          `,
        },
      }),

      transformResponse: (res: unknown) => pick<User[]>(res, "users"),

      providesTags: (result) =>
        result
          ? [
              ...result.map((u) => ({ type: "User" as const, id: u.id })),
              { type: "User" as const, id: "LIST" },
            ]
          : [{ type: "User" as const, id: "LIST" }],
    }),

    getUser: builder.query<User, string>({
      query: (id) => ({
        body: {
          query: `
            query User($id: String!) {
              user(id: $id) {
                ${USER_FIELDS}
              }
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<User>(res, "user"),

      providesTags: (_result, _err, id) => [{ type: "User", id }],
    }),

    // Always writes the CALLER's own preferences — the server takes no user id
    // — and only the keys supplied, so a partial update (the webapp sends just
    // `language`) can't wipe the ones the mobile app set.
    updateMyPreferences: builder.mutation<User, UserPreferences>({
      query: (input) => ({
        body: {
          query: `
            mutation UpdateMyPreferences($input: UserPreferencesInput!) {
              updateMyPreferences(input: $input) {
                ${USER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<User>(res, "updateMyPreferences"),

      invalidatesTags: ["User"],
    }),

    // Email-first "Add user" lookup — tells the caller whether this email is
    // an existing account (invite it, keep its password) or new (create it).
    findUserByEmail: builder.query<UserLookup, string>({
      query: (email) => ({
        body: {
          query: `
            query FindUserByEmail($email: String!) {
              findUserByEmail(email: $email) {
                exists
                id
                firstName
                lastName
                email
                alreadyInOrg
              }
            }
          `,
          variables: { email },
        },
      }),

      transformResponse: (res: unknown) => pick<UserLookup>(res, "findUserByEmail"),
    }),

    createUser: builder.mutation<User, CreateUserInput>({
      query: (input) => ({
        body: {
          query: `
            mutation CreateUser($input: CreateUserInput!) {
              createUser(input: $input) {
                ${USER_FIELDS}
              }
            }
          `,
          variables: { input },
        },
      }),

      transformResponse: (res: unknown) => pick<User>(res, "createUser"),

      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    updateUser: builder.mutation<User, { id: string; input: UpdateUserInput }>({
      query: ({ id, input }) => ({
        body: {
          query: `
            mutation UpdateUser($id: String!, $input: UpdateUserInput!) {
              updateUser(id: $id, input: $input) {
                ${USER_FIELDS}
              }
            }
          `,
          variables: { id, input },
        },
      }),

      transformResponse: (res: unknown) => pick<User>(res, "updateUser"),

      invalidatesTags: (_result, _err, { id }) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),

    deleteUser: builder.mutation<boolean, string>({
      query: (id) => ({
        body: {
          query: `
            mutation DeleteUser($id: String!) {
              deleteUser(id: $id)
            }
          `,
          variables: { id },
        },
      }),

      transformResponse: (res: unknown) => pick<boolean>(res, "deleteUser"),

      invalidatesTags: (_result, _err, id) => [
        { type: "User", id },
        { type: "User", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,
  useLazyFindUserByEmailQuery,
  useUpdateMyPreferencesMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
} = userApi;
